import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthCallback from "./pages/AuthCallback";
import PendingApproval from "./pages/PendingApproval";
import UserDashboard from "./pages/UserDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import DocumentView from "./pages/DocumentView";
import UsageDashboard from "./pages/UsageDashboard";
import ResearchPage from "./pages/ResearchPage";
import LibraryPage from "./pages/LibraryPage";
import LibraryDocumentView from "./pages/LibraryDocumentView";
import ClientsPage from "./pages/ClientsPage";
import InvoicesPage from "./pages/InvoicesPage";
import InvoiceFormPage from "./pages/InvoiceFormPage";
import InvoiceViewPage from "./pages/InvoiceViewPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Note: We use Bearer tokens (localStorage), NOT cookies for auth.
// withCredentials is NOT needed and can cause CORS issues with wildcard origins.

// Axios interceptor: attach session token from localStorage as Bearer header
// Requests with X-Skip-Auth header bypass token injection (used by auth/session exchange)
axios.interceptors.request.use((config) => {
  if (config.headers['X-Skip-Auth']) {
    delete config.headers['X-Skip-Auth'];
    return config;
  }
  const token = localStorage.getItem('session_token');
  if (token && token !== 'null' && token !== 'undefined') {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem('session_token');
    setUser(null);
    setIsAuthenticated(false);
    window.location.href = '/';
  };

  const updateUser = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      isAuthenticated, 
      login, 
      logout, 
      checkAuth,
      updateUser,
      API 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Helper: a "super_admin" is also an "admin" by privilege superset.
const _isAdminLike = (u) => u && (u.role === 'admin' || u.role === 'super_admin');

// Protected Route Component
const ProtectedRoute = ({ children, requireApproved = true, requireAdmin = false }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Check for user data passed from AuthCallback
  if (location.state?.user) {
    const passedUser = location.state.user;
    
    if (requireAdmin && !_isAdminLike(passedUser)) {
      return <Navigate to="/dashboard" replace />;
    }
    
    if (requireApproved && passedUser.status !== 'approved' && !_isAdminLike(passedUser)) {
      return <Navigate to="/pending" replace />;
    }
    
    return children;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (requireAdmin && !_isAdminLike(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireApproved && user?.status !== 'approved' && !_isAdminLike(user)) {
    return <Navigate to="/pending" replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id - synchronous check to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/pending" element={
        <ProtectedRoute requireApproved={false}>
          <PendingApproval />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <UserDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute requireAdmin={true}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/usage" element={
        <ProtectedRoute requireAdmin={true}>
          <UsageDashboard />
        </ProtectedRoute>
      } />
      <Route path="/research" element={
        <ProtectedRoute>
          <ResearchPage />
        </ProtectedRoute>
      } />
      <Route path="/library" element={
        <ProtectedRoute>
          <LibraryPage />
        </ProtectedRoute>
      } />
      <Route path="/library/:libDocId" element={
        <ProtectedRoute>
          <LibraryDocumentView />
        </ProtectedRoute>
      } />
      <Route path="/document/:docId" element={
        <ProtectedRoute>
          <DocumentView />
        </ProtectedRoute>
      } />
      <Route path="/clients" element={
        <ProtectedRoute requireAdmin={true}>
          <ClientsPage />
        </ProtectedRoute>
      } />
      <Route path="/invoices" element={
        <ProtectedRoute requireAdmin={true}>
          <InvoicesPage />
        </ProtectedRoute>
      } />
      <Route path="/invoices/new" element={
        <ProtectedRoute requireAdmin={true}>
          <InvoiceFormPage />
        </ProtectedRoute>
      } />
      <Route path="/invoices/:invoiceId" element={
        <ProtectedRoute requireAdmin={true}>
          <InvoiceViewPage />
        </ProtectedRoute>
      } />
      <Route path="/invoices/:invoiceId/edit" element={
        <ProtectedRoute requireAdmin={true}>
          <InvoiceFormPage />
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
